<script lang="ts">
	/**
	 * A link component that is designed to handle the link collection data received from directus.
	 * The component checks if the link is internal or custom and redirects the correct data to the generic
	 * Link component. The component can easily be used with spread props:
	 *
	 * <DirectusLink {...article.link} />
	 */
	import Media from '$lib/components/media/Media.svelte';
	import Link from '$lib/components/navigation/Link.svelte';

	type LinkType = 'internal' | 'custom' | 'category' | 'file' | 'collection' | 'product';

	interface Page {
		slug: string;
	}

	import { page as p } from '$app/stores';
	import { PUBLIC_DIRECTUS_BACKEND } from '$env/static/public';

	/*********
	 * Props *
	 **********/
	export let type: LinkType = 'custom'; // Type of the link ('custom' or 'internal')
	export let page: Page; // The page prop from directus, needed if type is internal
	export let parameters: string; // (Optional) link parameters
	export let link: string; // The link to use if type is custom
	export let file: any; // Used if type is file
	export let new_tab: boolean;
	export let aria_label: string; // (Optional) Label for accessibility if no text is displayed in link
	export let collection: string;
	export let product: string;

	let final_link: string = link;
	$: {
		if (type === 'internal' || (type === 'category' && page)) {
			final_link = page?.slug;
			if (parameters) {
				final_link += parameters;
			}
		} else if (type === 'file') {
			final_link = PUBLIC_DIRECTUS_BACKEND + 'assets/' + file?.id;
		} else if (type === 'collection') {
			try {
				const collectionData = JSON.parse(collection);
				final_link =
					'shop/' + (collectionData?.[0]?.selectedCollection?.node?.id.split('/').pop() ?? '');
			} catch (e) {}
		} else if (type === 'product') {
			try {
				const productData = JSON.parse(product);
				final_link = 'shop/' + (productData?.[0]?.selectedProduct?.node?.id.split('/').pop() ?? '');
			} catch (e) {}
		} else {
			final_link = link;
		}
	}
</script>

{#if type !== 'category' || page}
	<Link {type} link={final_link} {aria_label} {new_tab} on:click>
		<slot />
	</Link>
{:else}
	<slot />
{/if}

<style lang="scss">
	a {
		display: inline-flex;
		align-items: center;
		text-decoration: none;
	}

	.icon {
		height: 100%;

		&.before {
			margin-inline-end: 8px;
		}
		&.after {
			margin-inline-start: 8px;
		}
	}
</style>
